import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, forkJoin, of, from } from 'rxjs';
import { switchMap, first, map } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { SelectedCustomerService } from '../selected-customer/selected-customer.service';
import { GungFlowService } from '../gung-flow/gung-flow.service';
import { GungModalService } from '../gung-modal/gung-modal.service';
import { TranslateService } from '@ngx-translate/core';
import { DownloadFileService } from '../file-downloader/download-file.service';
import { Customer } from '../../models';
import { gungComparatorHelper } from '../../utils/gung-utils';

export enum ExportCustomerSelectionDataType {
  Xlsx,
  Csv
}

@Injectable({
  providedIn: 'root'
})

export class CustomerExportService {

  protected translateService = inject(TranslateService);
  protected downloadFileService = inject(DownloadFileService);
  protected http = inject(HttpClient);
  protected authService = inject(AuthService);
  protected selectedCustomerService = inject(SelectedCustomerService);
  protected gungFlowService = inject(GungFlowService);
  protected gungModalService = inject(GungModalService);

  currentLang = this.translateService.currentLang || 'se';

  exportExcel(
    customerList: Customer[],
    template?: string,
    additionalVariables?: {}
  ): Observable<{ status: boolean; message: string }> {
    customerList = this.sortExportCustomers(customerList);
    return this.downloadFile(
      'generate-customer-excel-from-customers-ids',
      customerList,
      ExportCustomerSelectionDataType.Xlsx,
      template,
      additionalVariables
    );
  }

  getExportRequestBody(
    exportSelectionDataType: ExportCustomerSelectionDataType,
    customerList: Customer[],
    template?: string,
    additionalVariables?: {},
    additionalParams?: {}
  ): Observable<any> {
    return forkJoin([
      this.authService.getCurrentUser().pipe(first()),
      this.selectedCustomerService.getSelectedCustomer().pipe(first()),
      this.gungFlowService.getSelectedFlow().pipe(first())
    ]).pipe(
      map(([user, customer, flow]) => {
        const requestBody: any = {
          ids: customerList.map(c => c.id),
          recipient: user.email,
          customerId: customer.id,
          flowId: flow.id,
          ...additionalVariables
        };

        // Add template/definitionId if provided
        if (template) {
          requestBody.definitionId = template;
        }

        return requestBody;
      })
    );
  }

  buildExportRequestFromModal(
    exportSelectionDataType: ExportCustomerSelectionDataType,
    customerList: Customer[],
    template?: string,
    additionalVariables?: {},
    additionalParams?: {}
  ): Observable<{ isValid: boolean; body: any }> {
    return this.getExportRequestBody(exportSelectionDataType, customerList, template, additionalVariables, additionalParams).pipe(
      first(),
      switchMap((data: any) => {
        return forkJoin({
          data: of(data),
          modalOutput: from(this.gungModalService.openConfirmExportModal(data.recipient)).pipe(first()),
          currentUser: this.authService.getCurrentUser().pipe(first())
        });
      }),
      switchMap(({ data, modalOutput, currentUser}) => {
        let isValid = false;

        if (modalOutput && modalOutput.email) {
          isValid = true;
          data.recipient = modalOutput.email;
          data.definitionId = modalOutput.definitionId;
          data.params = {
            ...modalOutput.params,
            user: currentUser
          };
        }

        return of({ isValid, body: data });
      })
    );
  }

  exportCustomersServiceCall(serviceUrl: string, body: any): Observable<{ status: boolean; message: string }> {
    return this.http.post<{ status: string }>('download/' + serviceUrl, body).pipe(
      first(),
      map(resp => {
        return {
          status: !resp || resp.status === 'OK',
          message: resp && resp.status
        };
      })
    );
  }

  downloadFile(
    serviceUrl: string,
    customerList: Customer[],
    type: ExportCustomerSelectionDataType,
    template?: string,
    additionalVariables?: {},
    additionalParams?: {}
  ): Observable<{ status: boolean; message: string }> {
    const requestToSendObs = this.buildExportRequestFromModal(
      type,
      customerList,
      template,
      additionalVariables,
      additionalParams
    );

    return requestToSendObs.pipe(
      first(),
      switchMap((request: { isValid: boolean; body: any }) => {
        if (request.isValid) {
          return this.exportCustomersServiceCall(serviceUrl, request.body);
        } else {
          return of({
            status: false,
            message: 'An error has occurred'
          });
        }
      })
    );
  }

  sortExportCustomers(customerList: Customer[]): Customer[] {
    return customerList.sort((a, b) => gungComparatorHelper(a.extra?.pim?.sequence, b.extra?.pim?.sequence, -1));
  }

}